import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Message,
  Divider,
  Icon,
  Progress,
  Segment,
  Header,
  List,
} from 'semantic-ui-react';
import _ from 'lodash';
import Papa from 'papaparse';
import axios from 'axios';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import { processInfluencerCSV, splitHeaderRow } from './instagram-utils';
import CsvRowsTable from './rows-table';
import hasRESTClient from '../../hocs/has-rest-client';

const MAX_INFLUENCERS_PER_CAMPAIGN = 150;
// A dying lambda never clears csvLoadStatus, so a run this old is treated as dead and stops
// blocking uploads. 20 min = the lambda's 15 min timeout plus 5 min of headroom.
const LOAD_STATUS_TTL_MS = 20 * 60 * 1000;

class CsvInfluencerUploadModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'upload', // 'upload', 'processing', 'preview', 'uploading', 'processing-server', 'complete'
      csvFile: null,
      rawData: null,
      processedData: null,
      headerValue: null,
      standardizedCsv: null,
      uploadProgress: 0,
      uploadedFileUrl: null,
      error: null,
      loadInProgress: false,
      liveInfluencerCount: null,
      liveHandles: null,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchLoadStatus();
    this.fetchLiveInfluencers();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  // The counts passed down as props come from the campaign in redux, which is only
  // refreshed on a full page load. Reading them live on open is what keeps the cap
  // honest after an import has added influencers behind the modal's back.
  getCurrentInfluencerCount = () => {
    const { liveInfluencerCount } = this.state;
    const { currentInfluencerCount } = this.props;
    return _.isFinite(liveInfluencerCount) ? liveInfluencerCount : currentInfluencerCount;
  };

  getExistingCampaignHandles = () => {
    const { liveHandles } = this.state;
    const { existingCampaignHandles } = this.props;
    return liveHandles || existingCampaignHandles || [];
  };

  fetchLoadStatus = async () => {
    const { restClient, campaignId } = this.props;

    try {
      const campaign = await restClient.get(`/campaigns/${campaignId}?fields[campaigns]=csvLoadStatus`);
      const csvLoadStatus = _.get(campaign, 'data.data.attributes.csvLoadStatus');
      const startedAt = new Date(_.get(csvLoadStatus, 'startedAt')).getTime();
      const isStale = !_.isFinite(startedAt) || Date.now() - startedAt > LOAD_STATUS_TTL_MS;

      // The request can outlive the modal - closing it before this resolves would
      // otherwise setState on an unmounted component.
      if (!this.mounted) return;

      this.setState({ loadInProgress: _.get(csvLoadStatus, 'running', false) && !isStale });
    } catch (error) {
      // A failed status check should never block an upload.
      // eslint-disable-next-line no-console
      console.error('Failed to load CSV load status', error);
    }
  };

  fetchLiveInfluencers = async () => {
    const { restClient, campaignId } = this.props;

    try {
      // page[limit]=1 still reports the real total in meta, so the count costs one row
      // rather than the whole list - discussions default to a 400 row page, which would
      // silently under-count any campaign larger than that.
      const counted = await restClient.get(
        `/discussions?filter[campaign]=${campaignId}&page[limit]=1&fields[discussions]=name`,
      );
      const total = _.get(counted, 'data.meta.page.total');

      if (!this.mounted || !_.isFinite(total)) return;

      this.setState({ liveInfluencerCount: total });

      // Handles only matter below the cap - at or above it the modal blocks regardless,
      // which also bounds this payload by the very cap being enforced.
      if (total >= MAX_INFLUENCERS_PER_CAMPAIGN) return;

      if (total === 0) {
        this.setState({ liveHandles: [] });
        return;
      }

      const withHandles = await restClient.get(
        `/discussions?filter[campaign]=${campaignId}&include=influencer`
        + `&fields[influencer]=instagram&page[limit]=${total}`,
      );
      const handles = _.compact(_.map(
        _.get(withHandles, 'data.included', []),
        included => _.toLower(_.get(included, 'attributes.instagram')),
      ));

      if (!this.mounted) return;

      this.setState({ liveHandles: handles });
    } catch (error) {
      // Falling back to the props leaves the pre-existing behaviour intact.
      // eslint-disable-next-line no-console
      console.error('Failed to load live influencer counts', error);
    }
  };

  handleFileChange = event => {
    const file = _.get(event, 'target.files[0]');
    if (!file) return;

    // Clear any previous errors
    this.setState({ error: null });

    if (!_.endsWith(_.toLower(file.name), '.csv')) {
      this.setState({
        error: 'Please select a CSV file',
        step: 'upload',
      });
      // Clear the file input
      event.target.value = '';
      return;
    }

    this.setState({
      csvFile: file,
      step: 'processing',
      error: null,
    });

    // Parsed without a header so no row is consumed silently - splitHeaderRow decides
    // whether the first row is a header. Values stay strings so handles are kept as typed.
    Papa.parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      delimiter: '', // Let Papa Parse auto-detect, but don't error on warnings
      complete: results => {
        // Only error on actual parsing errors, not delimiter detection warnings
        const realErrors = results.errors.filter(error =>
          error.type !== 'Delimiter' && error.type !== 'FieldMismatch',
        );
        if (realErrors.length > 0) {
          this.setState({
            step: 'upload',
            error: `CSV parsing error: ${_.get(realErrors, '[0].message', 'Unknown error')}`,
          });
          return;
        }

        const { headerValue, dataRows } = splitHeaderRow(results.data);

        if (_.isEmpty(dataRows)) {
          this.setState({
            step: 'upload',
            error: 'CSV file appears to be empty',
          });
          return;
        }

        const processedData = processInfluencerCSV(
          dataRows,
          this.getCurrentInfluencerCount(),
          MAX_INFLUENCERS_PER_CAMPAIGN,
          this.getExistingCampaignHandles(),
          _.isNull(headerValue) ? 1 : 2,
        );

        this.setState({
          rawData: results.data,
          processedData,
          headerValue,
          step: 'preview',
          error: null,
        });
      },
      error: error => {
        this.setState({
          step: 'upload',
          error: `Error reading file: ${_.get(error, 'message', 'Unknown error')}`,
        });
      },
    });
  };

  generateStandardizedCSV = validInfluencers => {
    const { campaignId } = this.props;
    // Remove duplicates first, then take only first MAX_INFLUENCERS_PER_CAMPAIGN
    const uniqueInfluencers = _.uniqBy(validInfluencers, 'instagramHandle');
    const limitedInfluencers = _.take(uniqueInfluencers, MAX_INFLUENCERS_PER_CAMPAIGN);

    // Create standardized CSV data with canonical Instagram URLs
    const csvData = _.map(limitedInfluencers, influencer => ({
      instagramUrl: `https://instagram.com/${influencer.instagramHandle}`,
    }));

    // Convert to CSV string
    const csv = Papa.unparse(csvData, {
      header: true,
      columns: ['instagramUrl'],
    });

    // Create blob for upload with timestamp in filename
    const timestamp = Date.now();
    const fileName = `${campaignId}-influencers-${timestamp}.csv`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const file = new File([blob], fileName, { type: 'text/csv' });

    return { csvString: csv, file, data: csvData, fileName };
  };

  uploadToS3 = async (file, fileName) => {
    try {
      const s3Url = `https://s3-eu-west-1.amazonaws.com/discussions-uploads-production/${fileName}`;

      await axios.put(s3Url, file, {
        headers: {
          'Content-Type': 'text/csv',
        },
        onUploadProgress: progressEvent => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          this.setState({ uploadProgress: progress });
        },
      });

      return {
        url: s3Url,
        fileName,
      };
    } catch (error) {
      throw new Error(`S3 upload failed: ${error.message}`);
    }
  };

  processInfluencersOnServer = async s3Url => {
    const { campaignId, userEmail, userInfo } = this.props;

    try {
      const response = await axios.post(
        `https://papi.dyzio.co/papi/secure/campaign/${campaignId}/csv`,
        {
          data: {
            filename: s3Url,
            userEmail,
            userInfo,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (error) {
      throw new Error(`Server processing failed: ${error.message}`);
    }
  };

  handleUploadToS3 = async () => {
    const { processedData } = this.state;
    const { campaignCsvUploads, restClient, campaignId } = this.props;

    if (!processedData || processedData.summary.newToUpload === 0) {
      return;
    }

    const toUpload = _.take(
      processedData.uniqueNewInfluencers,
      processedData.summary.availableSlots,
    );
    const standardizedCsv = this.generateStandardizedCSV(toUpload);

    this.setState({
      step: 'uploading',
      standardizedCsv,
      uploadProgress: 0,
    });

    try {
      // Step 1: Upload to S3
      const uploadResult = await this.uploadToS3(standardizedCsv.file, standardizedCsv.fileName);

      this.setState({
        uploadedFileUrl: uploadResult.url,
        step: 'processing-server',
      });

      // Step 2: Patch campaign's "csvFileUploads" and "csvLoadStatus" fields
      const campaign = await restClient.get(`/campaigns/${campaignId}?fields[campaigns]=csvFileUploads`);
      const currentCsvFiles = _.get(campaign, 'data.data.attributes.csvFileUploads', []);

      await restClient.patch(`campaigns/${campaignId}`, {
        data: {
          attributes: {
            csvFileUploads: [...currentCsvFiles, uploadResult.fileName],
            csvLoadStatus: {
              running: true,
              startedAt: new Date().toISOString(),
              fileName: uploadResult.fileName,
            },
          },
        },
      });

      // Step 3: Fire and forget - start server processing but don't wait
      this.processInfluencersOnServer(uploadResult.url).catch(error => {
        console.error('Server processing error (expected timeout):', error);
      });

      // Immediately show completion
      this.setState({
        step: 'complete',
      });
    } catch (error) {
      this.setState({
        step: 'preview',
        error: `Upload failed: ${error.message}`,
      });
    }
  };

  resetUpload = () => {
    this.setState({
      step: 'upload',
      csvFile: null,
      rawData: null,
      processedData: null,
      headerValue: null,
      standardizedCsv: null,
      uploadProgress: 0,
      uploadedFileUrl: null,
      error: null,
    });
  };

  renderUploadStep = () => {
    const { loadInProgress } = this.state;
    const currentInfluencerCount = this.getCurrentInfluencerCount();
    const availableSlots = Math.max(0, MAX_INFLUENCERS_PER_CAMPAIGN - currentInfluencerCount);
    const isAtCapacity = availableSlots === 0;
    const isSelectDisabled = isAtCapacity || loadInProgress;

    const textStyle = { margin: '0 0 8px 0' };
    const buttonStyle = { marginTop: '10px' };
    const inProgressStyle = { textAlign: 'left', marginTop: '16px' };

    return (
      <div>
        <Header as="h3">
          Choose a CSV file
          <Header.Subheader>
            The Instagram handles in it are listed for you to review before anything is added.
          </Header.Subheader>
        </Header>

        {isAtCapacity && (
          <Message negative>
            <Message.Header>Campaign at Maximum Capacity</Message.Header>
            <p>
              This campaign already has {currentInfluencerCount} influencers,
              which is the maximum allowed ({MAX_INFLUENCERS_PER_CAMPAIGN}).
              You cannot add more influencers to this campaign.
            </p>
          </Message>
        )}

        <Segment placeholder textAlign="center">
          <div>
            <p style={textStyle}>
              {`This campaign has ${currentInfluencerCount} influencers`
                + ` · ${availableSlots} more can be added (maximum ${MAX_INFLUENCERS_PER_CAMPAIGN})`}
            </p>
            <Button
              primary
              as="label"
              htmlFor="csv-file-input"
              disabled={isSelectDisabled}
              size="large"
              style={buttonStyle}
            >
              <Icon name="upload" />
              Select CSV File
            </Button>
            <input
              id="csv-file-input"
              key={this.state.step}
              type="file"
              accept=".csv"
              onChange={this.handleFileChange}
              style={{ display: 'none' }}
              disabled={isSelectDisabled}
            />

            {loadInProgress && (
              <Message warning icon style={inProgressStyle}>
                <Icon name="notched circle" loading />
                <Message.Content>
                  <Message.Header>A CSV import is already running</Message.Header>
                  <p>
                    An earlier upload for this campaign is still being processed.
                    Please wait for it to finish before uploading another file.
                  </p>
                </Message.Content>
              </Message>
            )}
          </div>
        </Segment>

        <Message info>
          <Message.Header>CSV Format Requirements</Message.Header>
          <List bulleted>
            <List.Item>
              <strong>Limit:</strong> Maximum of {MAX_INFLUENCERS_PER_CAMPAIGN} Instagram influencers per campaign.
            </List.Item>
            <List.Item>
              <strong>Single column:</strong> One column with Instagram handles or URLs
            </List.Item>
            <List.Item>
              <strong>Header:</strong> Optional - a first row like
              {' '}&quot;Instagram&quot; or &quot;Handle&quot; is skipped
            </List.Item>
            <List.Item>
              <strong>Data:</strong> One Instagram handle per row
            </List.Item>
          </List>
        </Message>

        <Message>
          <Message.Header>Supported Instagram Formats</Message.Header>
          <List bulleted>
            <List.Item>Simple handles: <code>kimkardashian</code></List.Item>
            <List.Item>With @ symbol: <code>@kimkardashian</code></List.Item>
            <List.Item>Full URLs: <code>https://instagram.com/kimkardashian</code></List.Item>
            <List.Item>Short URLs: <code>instagram.com/kimkardashian</code></List.Item>
          </List>
        </Message>
      </div>
    );
  };

  renderProcessingStep = () => (
    <div>
      <Header as="h3">
        <Icon name="cog" loading />
        Processing CSV Data
      </Header>
      <Progress percent={50} indicating>
        Extracting and validating Instagram handles...
      </Progress>
    </div>
  );

  renderPreviewStep = () => {
    const { processedData, headerValue } = this.state;

    if (!processedData) return null;

    const { results, summary } = processedData;
    const rows = _.isNull(headerValue)
      ? results
      : [{ originalRow: 1, originalInstagramInput: headerValue, status: 'header' }, ...results];
    const pluralize = (count, one, many) => `${count} ${count === 1 ? one : many}`;
    const summaryLine = _.compact([
      pluralize(_.size(rows), 'row', 'rows'),
      `${_.min([summary.newToUpload, summary.availableSlots])} will be added`,
      summary.invalid > 0 && `${summary.invalid} ${summary.invalid === 1 ? 'needs' : 'need'} fixing`,
    ]).join(' · ');

    // If over limit, show only the error message
    if (summary.overLimit) {
      const headerStyle = { fontSize: '18px', marginBottom: '15px' };
      const textStyle = { fontSize: '15px', lineHeight: '1.6' };
      const actionStyle = { fontSize: '15px', marginTop: '15px' };

      return (
        <div>
          <Message negative size="large">
            <Message.Header style={headerStyle}>
              <Icon name="exclamation triangle" />
              Campaign Limit Exceeded
            </Message.Header>
            <p style={textStyle}>
              This campaign currently has <strong>{summary.currentInfluencerCount}</strong> influencers.
              Your CSV contains <strong>{summary.validAfterDedup}</strong> new influencers.
              Adding them would bring the total to <strong>{summary.totalAfterUpload}</strong>,
              which exceeds the maximum of <strong>{summary.maxInfluencers}</strong>.
            </p>
            <Divider />
            <p style={actionStyle}>
              <Icon name="lightbulb" />
              <strong>You can add up to {summary.availableSlots} more influencer(s).</strong>
              {' '}Please reduce your CSV and try again.
            </p>
          </Message>
        </div>
      );
    }

    return (
      <div>
        <Header as="h3">
          Review your list
          <Header.Subheader>{summaryLine}</Header.Subheader>
        </Header>

        <CsvRowsTable rows={rows} />

        {summary.valid === 0 && (
          <Message negative>
            <Message.Header>No valid influencers found</Message.Header>
            <p>Please check your CSV format and try again. Make sure you have a column with Instagram handles or URLs.</p>
          </Message>
        )}

        {summary.valid > 0 && summary.newToUpload === 0 && (
          <Message info>
            <Message.Header>All handles already on campaign</Message.Header>
            <p>All handles in this CSV are already on the campaign. Nothing to upload.</p>
          </Message>
        )}
      </div>
    );
  };

  renderUploadingStep = () => {
    const { standardizedCsv, uploadProgress } = this.state;

    return (
      <div>
        <Header as="h3">
          <Icon name="cloud upload" color="blue" />
          Uploading Standardized CSV
        </Header>

        <Progress percent={uploadProgress} indicating active>
          Uploading to S3... {uploadProgress}%
        </Progress>

        {standardizedCsv && (
          <Message info>
            <Message.Header>Uploading directly to S3</Message.Header>
            <p>Converting {_.size(standardizedCsv.data)} valid influencers to canonical Instagram URLs.</p>
            <p><strong>File:</strong> {standardizedCsv.fileName}</p>
            <p><strong>Size:</strong> {Math.round(standardizedCsv.file.size / 1024)} KB</p>
            <p><strong>S3 Bucket:</strong> discussions-uploads-production</p>
          </Message>
        )}
      </div>
    );
  };

  renderProcessingServerStep = () => (
    <div>
      <Header as="h3">
        <Icon name="server" color="blue" />
        Starting Processing
      </Header>

      <Progress percent={95} indicating active>
        Initiating server processing...
      </Progress>

      <Message info>
        <Message.Header>Starting background processing</Message.Header>
        <p>Your CSV has been uploaded successfully. Starting the processing job now...</p>
      </Message>
    </div>
  );

  renderCompleteStep = () => {
    const { uploadedFileUrl } = this.state;
    const { userEmail } = this.props;

    return (
      <div>
        <Header as="h3">
          <Icon name="check circle" color="green" />
          Upload Complete
        </Header>
        <Progress percent={100} color="green">
          Upload successful!
        </Progress>
        <Message success>
          <Message.Header>Upload successful</Message.Header>
          <p><strong>Your influencers are being processed now.</strong></p>
          <p>There can still be errors if handles cannot be found. You will be emailed a detailed status report at <strong>{userEmail}</strong>.</p>

          {uploadedFileUrl && (
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              <strong>File uploaded:</strong> {uploadedFileUrl.split('/').pop()}
            </p>
          )}
        </Message>
      </div>
    );
  };

  render() {
    const { onClose, isSubmitting } = this.props;
    const { step, processedData, error } = this.state;

    const isOverLimit = _.get(processedData, 'summary.overLimit', false);
    const canImport = processedData && _.get(processedData, 'summary.newToUpload', 0) > 0 && !isOverLimit;
    const toAddCount = _.min([
      _.get(processedData, 'summary.newToUpload', 0),
      _.get(processedData, 'summary.availableSlots', 0),
    ]);

    return (
      <DyzioModal
        headerText="Import Influencers from CSV"
        size="large"
        onClose={onClose}
      >
        <div>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {step === 'upload' && this.renderUploadStep()}
          {step === 'processing' && this.renderProcessingStep()}
          {step === 'preview' && this.renderPreviewStep()}
          {step === 'uploading' && this.renderUploadingStep()}
          {step === 'processing-server' && this.renderProcessingServerStep()}
          {step === 'complete' && this.renderCompleteStep()}

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {step === 'preview' && (
              <Button
                onClick={this.resetUpload}
                disabled={isSubmitting}
              >
                <Icon name="arrow left" />
                Upload Different File
              </Button>
            )}

            {step !== 'preview' && step !== 'complete' && <div />}

            {step === 'preview' && !isOverLimit && (
              <Button
                color="pink"
                disabled={!canImport || isSubmitting}
                loading={isSubmitting}
                onClick={this.handleUploadToS3}
                size="medium"
              >
                <Icon name="cloud upload" />
                {toAddCount > 0
                  ? `Add ${toAddCount} ${toAddCount === 1 ? 'influencer' : 'influencers'} to the campaign`
                  : 'No influencers to add'}
              </Button>
            )}

            {step === 'preview' && isOverLimit && <div />}

            {step === 'complete' && (
              <Button
                color="green"
                onClick={onClose}
                size="medium"
                style={{ margin: '0 auto' }}
              >
                <Icon name="check" />
                OK
              </Button>
            )}
          </div>
        </div>
      </DyzioModal>
    );
  }
}

CsvInfluencerUploadModal.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  campaignId: PropTypes.string,
  userEmail: PropTypes.string,
  currentInfluencerCount: PropTypes.number,
  existingCampaignHandles: PropTypes.arrayOf(PropTypes.string),
};

CsvInfluencerUploadModal.defaultProps = {
  onClose: _.noop,
  onSubmit: _.noop,
  isSubmitting: false,
  currentInfluencerCount: 0,
  existingCampaignHandles: [],
};

export default hasRESTClient(CsvInfluencerUploadModal);



/** WEBPACK FOOTER **
 ** ./src/components/csv-upload-modal/index.js
 **/